import type {
  PurchaseJobPlBucket,
  PurchaseOverheadGroup,
  PurchaseRequisition,
  PurchaseRequisitionStatus,
} from '../types/purchasing';
import { purchaseOrderNumbers } from './purchaseLineVendors';
import { resolveSpendClass } from './purchasingSpendClass';
import { purchaseListStatus } from './purchasingStatus';

export const PURCHASE_RESERVED_STATUSES = new Set<PurchaseRequisitionStatus>([
  'pending_approval',
  'in_review',
]);

export const PURCHASE_COMMITTED_STATUSES = new Set<PurchaseRequisitionStatus>([
  'approved',
  'purchased',
]);

export type PurchaseCommitmentKind = 'reserved' | 'committed' | null;

export function purchaseCommitmentKind(status: PurchaseRequisitionStatus): PurchaseCommitmentKind {
  if (PURCHASE_RESERVED_STATUSES.has(status)) return 'reserved';
  if (PURCHASE_COMMITTED_STATUSES.has(status)) return 'committed';
  return null;
}

export function chicagoYearMonth(date: Date): { year: number; month: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: 'numeric',
  }).formatToParts(date);
  const year = Number(parts.find(p => p.type === 'year')?.value);
  const month = Number(parts.find(p => p.type === 'month')?.value);
  return {
    year: Number.isFinite(year) ? year : date.getFullYear(),
    month: Number.isFinite(month) ? month : date.getMonth() + 1,
  };
}

export function periodKey(parts: { year: number; month: number }): string {
  return `${parts.year}-${String(parts.month).padStart(2, '0')}`;
}

/** Reserved uses createdAt; committed uses approvedAt (fallback createdAt). */
export function purchasePeriodDate(row: Pick<PurchaseRequisition, 'status' | 'createdAt' | 'approvedAt'>): Date {
  const kind = purchaseCommitmentKind(row.status);
  if (kind === 'committed' && row.approvedAt) return row.approvedAt;
  return row.createdAt;
}

export function sameChicagoMonth(date: Date, now: Date): boolean {
  return sameChicagoPeriod(date, now, 'monthly');
}

export function chicagoQuarter(month: number): number {
  return Math.ceil(month / 3);
}

export function sameChicagoPeriod(
  date: Date,
  now: Date,
  interval: 'monthly' | 'quarterly' | 'annual' = 'monthly',
): boolean {
  const a = chicagoYearMonth(date);
  const b = chicagoYearMonth(now);
  if (a.year !== b.year) return false;
  if (interval === 'annual') return true;
  if (interval === 'quarterly') return chicagoQuarter(a.month) === chicagoQuarter(b.month);
  return a.month === b.month;
}

export function opexPeriodLabel(
  now: Date,
  interval: 'monthly' | 'quarterly' | 'annual' = 'monthly',
): string {
  const parts = chicagoYearMonth(now);
  if (interval === 'annual') return String(parts.year);
  if (interval === 'quarterly') return `${parts.year}-Q${chicagoQuarter(parts.month)}`;
  return periodKey(parts);
}

function rowAmount(row: Pick<PurchaseRequisition, 'amount' | 'status' | 'purchasedAmount'>): number {
  if (row.status === 'purchased' && Number.isFinite(row.purchasedAmount)) return row.purchasedAmount as number;
  return Number.isFinite(row.amount) ? row.amount : 0;
}

export function resolvePurchaseDealId(row: Pick<PurchaseRequisition, 'dealId' | 'costObject'>): string | undefined {
  return row.dealId || row.costObject.dealId;
}

export function resolvePurchasePlBucket(
  row: Pick<PurchaseRequisition, 'plBucket' | 'costObject'>,
): PurchaseJobPlBucket | undefined {
  return row.plBucket || row.costObject.plBucket;
}

export function resolvePurchaseOverheadGroup(
  row: Pick<PurchaseRequisition, 'overheadGroup' | 'costObject'>,
): PurchaseOverheadGroup | undefined {
  return row.overheadGroup || row.costObject.overheadGroup;
}

export interface OpexGroupMonthSummary {
  periodLabel: string;
  interval: 'monthly' | 'quarterly' | 'annual';
  budgetedUsd: number | null;
  reservedUsd: number;
  committedUsd: number;
  remainingUsd: number | null;
  budgetUnset: boolean;
}

function countsTowardOpexGroup(
  row: PurchaseRequisition,
  group: PurchaseOverheadGroup,
  now: Date,
  interval: 'monthly' | 'quarterly' | 'annual',
  excludeId?: string,
): boolean {
  if (excludeId && row.id === excludeId) return false;
  if ((row.overheadExpenseId || row.costObject.overheadExpenseId || '').trim()) return false;
  if (resolveSpendClass(row.costObject) !== 'overhead') return false;
  if (resolvePurchaseOverheadGroup(row) !== group) return false;
  if (!purchaseCommitmentKind(row.status)) return false;
  return sameChicagoPeriod(purchasePeriodDate(row), now, interval);
}

export function summarizeOpexGroupMonth(input: {
  rows: PurchaseRequisition[];
  group: PurchaseOverheadGroup;
  budgetedUsd: number | null;
  interval?: 'monthly' | 'quarterly' | 'annual';
  now?: Date;
  excludeId?: string;
}): OpexGroupMonthSummary {
  const now = input.now || new Date();
  const interval = input.interval || 'monthly';
  let reservedUsd = 0;
  let committedUsd = 0;
  for (const row of input.rows) {
    if (!countsTowardOpexGroup(row, input.group, now, interval, input.excludeId)) continue;
    const kind = purchaseCommitmentKind(row.status);
    if (kind === 'reserved') reservedUsd += rowAmount(row);
    else committedUsd += rowAmount(row);
  }
  const used = reservedUsd + committedUsd;
  const remainingUsd = input.budgetedUsd == null ? null : input.budgetedUsd - used;
  return {
    periodLabel: opexPeriodLabel(now, interval),
    interval,
    budgetedUsd: input.budgetedUsd,
    reservedUsd,
    committedUsd,
    remainingUsd,
    budgetUnset: input.budgetedUsd == null,
  };
}

export interface JobNodeBudgetSummary {
  budgetUsd: number;
  actualUsd: number;
  reservedUsd: number;
  committedUsd: number;
  remainingUsd: number;
}

/** Budget cards read proposalBuilders/{dealId}. A timber project id stored as the deal has to be swapped first. */
export function proposalDealIdForBudget(
  storedDealId: string | undefined | null,
  projects: Array<{ id?: string; dealId?: string | null }>,
): string | undefined {
  const id = String(storedDealId || '').trim();
  if (!id) return undefined;
  const project = projects.find(p => p.id === id);
  const linked = String(project?.dealId || '').trim();
  if (linked && linked !== id) return linked;
  return id;
}

function countsTowardJobNode(
  row: PurchaseRequisition,
  dealId: string,
  plBucket: PurchaseJobPlBucket,
  excludeId?: string,
): boolean {
  if (excludeId && row.id === excludeId) return false;
  if (resolveSpendClass(row.costObject) !== 'job') return false;
  if (resolvePurchaseDealId(row) !== dealId) return false;
  if (resolvePurchasePlBucket(row) !== plBucket) return false;
  return purchaseCommitmentKind(row.status) != null;
}

export function summarizeJobNode(input: {
  rows: PurchaseRequisition[];
  dealId: string;
  plBucket: PurchaseJobPlBucket;
  budgetUsd: number;
  actualUsd: number;
  excludeId?: string;
}): JobNodeBudgetSummary {
  let reservedUsd = 0;
  let committedUsd = 0;
  for (const row of input.rows) {
    if (!countsTowardJobNode(row, input.dealId, input.plBucket, input.excludeId)) continue;
    const kind = purchaseCommitmentKind(row.status);
    if (kind === 'reserved') reservedUsd += rowAmount(row);
    else if (kind === 'committed') committedUsd += rowAmount(row);
  }
  return {
    budgetUsd: input.budgetUsd,
    actualUsd: input.actualUsd,
    reservedUsd,
    committedUsd,
    remainingUsd: input.budgetUsd - input.actualUsd - reservedUsd - committedUsd,
  };
}

export function amountExceedsRemaining(amountUsd: number, remainingUsd: number | null): boolean {
  if (remainingUsd == null) return false;
  return amountUsd - remainingUsd > 0.005;
}

/**
 * Live over-budget flag for the Purchasing UI.
 * Unset OpEx envelopes (`remainingUsd == null`) never flag.
 * Job nodes wait until the proposal budget snapshot is ready so a loading
 * `budgetUsd: 0` does not false-trigger the banner or submit block (CRM #76 follow-up).
 */
export function purchaseIsOverBudget(input: {
  paysForecastLine?: boolean;
  opexRemainingUsd?: number | null;
  jobRemainingUsd?: number | null;
  jobBudgetReady?: boolean;
  amountUsd: number;
}): boolean {
  if (input.paysForecastLine) return false;
  if (input.opexRemainingUsd !== undefined) {
    return amountExceedsRemaining(input.amountUsd, input.opexRemainingUsd);
  }
  if (input.jobRemainingUsd == null) return false;
  if (input.jobBudgetReady === false) return false;
  return amountExceedsRemaining(input.amountUsd, input.jobRemainingUsd);
}

/** Banner copy on the approval map when the live amount exceeds remaining. */
export function purchaseOverBudgetBanner(canOverride: boolean): string {
  return canOverride
    ? 'Over remaining budget — leadership can still submit, issue, or approve.'
    : 'Over remaining budget — flagged for approval; an approver can still clear it.';
}

export interface BudgetPurchaseLine {
  id: string;
  /** Issued PO number(s), or the PR number when nothing has been issued yet. */
  number: string;
  title: string;
  vendorName: string;
  /** Amount counted in reserved or committed. Receipt total once purchased. */
  amountUsd: number;
  /** Original PO amount when a purchased receipt differs from it. */
  poAmountUsd?: number;
  kind: 'reserved' | 'committed';
  statusId: string;
  statusLabel: string;
  at: Date;
}

function toBudgetPurchaseLine(row: PurchaseRequisition): BudgetPurchaseLine {
  const kind = purchaseCommitmentKind(row.status);
  const status = purchaseListStatus(row);
  const amountUsd = rowAmount(row);
  const numbers = purchaseOrderNumbers(row);
  const poAmountUsd =
    row.status === 'purchased' &&
    Number.isFinite(row.purchasedAmount) &&
    Math.abs((row.purchasedAmount as number) - row.amount) > 0.005
      ? row.amount
      : undefined;
  return {
    id: row.id,
    number: numbers.join(', ') || row.prNumber,
    title: row.title.trim(),
    vendorName: row.vendorName?.trim() || '',
    amountUsd,
    ...(poAmountUsd != null ? { poAmountUsd } : {}),
    kind: kind || 'committed',
    statusId: status.id,
    statusLabel: status.label,
    at: purchasePeriodDate(row),
  };
}

function byNewestPurchase(a: BudgetPurchaseLine, b: BudgetPurchaseLine): number {
  return b.at.getTime() - a.at.getTime() || a.number.localeCompare(b.number);
}

/** Reserved and committed purchases that make up an OpEx group's period totals. */
export function listOpexGroupPurchases(input: {
  rows: PurchaseRequisition[];
  group: PurchaseOverheadGroup;
  interval?: 'monthly' | 'quarterly' | 'annual';
  now?: Date;
  excludeId?: string;
}): BudgetPurchaseLine[] {
  const now = input.now || new Date();
  const interval = input.interval || 'monthly';
  return input.rows
    .filter(row => countsTowardOpexGroup(row, input.group, now, interval, input.excludeId))
    .map(toBudgetPurchaseLine)
    .sort(byNewestPurchase);
}

/** Reserved and committed purchases that make up a project node's totals. */
export function listJobNodePurchases(input: {
  rows: PurchaseRequisition[];
  dealId: string;
  plBucket: PurchaseJobPlBucket;
  excludeId?: string;
}): BudgetPurchaseLine[] {
  return input.rows
    .filter(row => countsTowardJobNode(row, input.dealId, input.plBucket, input.excludeId))
    .map(toBudgetPurchaseLine)
    .sort(byNewestPurchase);
}

export interface CommittedPurchaseLine {
  id: string;
  label: string;
  amount: number;
  vendorName?: string;
  title?: string;
}

export function committedJobPurchases(input: {
  rows: PurchaseRequisition[];
  dealId: string;
  plBucket: PurchaseJobPlBucket;
}): CommittedPurchaseLine[] {
  return input.rows
    .filter(row => {
      if (purchaseCommitmentKind(row.status) !== 'committed') return false;
      if (resolveSpendClass(row.costObject) !== 'job') return false;
      if (resolvePurchaseDealId(row) !== input.dealId) return false;
      return resolvePurchasePlBucket(row) === input.plBucket;
    })
    .map(row => ({
      id: row.id,
      label: row.poNumber || row.prNumber,
      amount: rowAmount(row),
      vendorName: row.vendorName,
      title: row.title,
    }));
}
